// ─── Date & File Formatting ──────────────────────────────────────────────────

import dayjs from 'dayjs'

export function formatDate(time) {
  if (!time) return ''
  return dayjs(time).tz('America/Los_Angeles').format('M/D/YYYY, h:mm a z')
}

export function isOlderThanDays(time, days = 7) {
  if (!time) return false
  return dayjs().diff(dayjs(time), 'day') >= days
}

export function formatFileSize(bytes) {
  if (bytes == null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}

// ─── Schedule & Cron Utilities ───────────────────────────────────────────────

/**
 * Creates a daily cron expression from hour and minute
 * @param {Number} hour - Hour (0-23)
 * @param {Number} minute - Minute (0-59)
 * @returns {String} Cron expression in format: cron(minute hour * * ? *)
 */
export function createCronExpression(hour, minute) {
  return `cron(${minute} ${hour} * * ? *)`;
}

/**
 * Parses a cron expression to extract hour and minute
 * @param {String} cronExpression - Cron expression in format: cron(minute hour * * ? *)
 * @returns {Object|null} Object with hour and minute properties, or null if invalid format
 */
export function parseCronExpression(cronExpression) {
  if (!cronExpression) {
    return null;
  }

  const cronRegex = /cron\((\d+)\s+(\d+)\s+\*\s+\*\s+\?\s+\*\)/;
  const match = cronExpression.match(cronRegex);

  if (match && match.length === 3) {
    return {
      minute: parseInt(match[1], 10),
      hour: parseInt(match[2], 10)
    };
  }

  return null;
}

/**
 * Formats a timezone string to a more user-friendly display format
 * @param {String} timezone - Timezone string (e.g., 'America/New_York')
 * @returns {String} Formatted timezone display (e.g., 'Eastern')
 */
export function formatTimezone(timezone) {
  if (!timezone) {
    return '';
  }

  const timezoneMap = {
    'America/New_York': 'Eastern',
    'America/Chicago': 'Central',
    'America/Denver': 'Mountain',
    'America/Los_Angeles': 'Pacific',
    'America/Honolulu': 'Hawaii',
    'America/Anchorage': 'Alaska',
    'America/Phoenix': 'Arizona'
  };

  return timezoneMap[timezone] || timezone.split('/').pop();
}

/**
 * Converts a 12-hour time format to 24-hour format
 * @param {Number} hour - Hour in 12-hour format (1-12)
 * @param {String} ampm - 'am' or 'pm'
 * @returns {Number} Hour in 24-hour format (0-23)
 */
export function convert12to24(hour, ampm) {
  hour = parseInt(hour, 10);
  if (ampm === 'pm' && hour < 12) {
    return hour + 12;
  } else if (ampm === 'am' && hour === 12) {
    return 0;
  }
  return hour;
}

/**
 * Converts a 24-hour time format to 12-hour format with AM/PM
 * @param {Number} hour - Hour in 24-hour format (0-23)
 * @param {Number} minute - Minute (0-59)
 * @returns {Object} Object with hour (1-12), minute, and ampm ('am' or 'pm')
 */
export function convert24to12(hour, minute) {
  let hour12 = hour % 12;
  hour12 = hour12 === 0 ? 12 : hour12;

  return {
    hour: hour12,
    minute: minute,
    ampm: hour < 12 ? 'am' : 'pm'
  };
}

/**
 * Formats time in 12-hour format with AM/PM
 * @param {Number} hour - Hour in 12-hour format (1-12)
 * @param {Number} minute - Minute (0-59)
 * @param {String} ampm - 'am' or 'pm'
 * @returns {String} Formatted time string (e.g., '6:15 PM')
 */
export function formatTime(hour, minute, ampm) {
  const hourStr = hour.toString();
  const minuteStr = minute.toString().padStart(2, '0');
  const ampmStr = ampm.toUpperCase();
  return `${hourStr}:${minuteStr} ${ampmStr}`;
}

/**
 * Formats a schedule time with timezone for display
 * @param {Object} schedule - Schedule object with ScheduleExpression and ScheduleExpressionTimezone
 * @returns {String} Formatted time with timezone (e.g., '6:15 PM Eastern')
 */
export function formatScheduleTimeWithTimezone(schedule) {
  if (!schedule) return 'N/A';

  const timezone = schedule.ScheduleExpressionTimezone;
  const scheduleExpression = schedule.ScheduleExpression;
  const parsedCron = parseCronExpression(scheduleExpression);

  if (!parsedCron) return 'N/A';

  const { hour, minute, ampm } = convert24to12(parsedCron.hour, parsedCron.minute);
  const formattedTime = formatTime(hour, minute, ampm);
  const selectedTimezone = formatTimezone(timezone) || 'Pacific';

  return `${formattedTime} ${selectedTimezone}`;
}
